import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { FadeUp, SectionLabel } from "../components/Motion";
import { getProjects } from "../services/projectsApi";
import { mapApiProject } from "../utils/projectMapper";

function groupFromPath(pathname) {
  if (pathname.endsWith("/ongoing")) return "ongoing";
  if (pathname.endsWith("/completed")) return "completed";
  return "all";
}

export default function Projects() {
  const location = useLocation();
  const group = groupFromPath(location.pathname);

  const [projects, setProjects] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadProjects() {
      try {
        setLoading(true);
        setError("");

        const apiProjects = await getProjects();
        const mappedProjects = apiProjects.map(mapApiProject);
        console.log("API Projects:", apiProjects);
        console.log(mappedProjects);

        setProjects(mappedProjects);
      } catch (err) {
        console.error("Project loading error:", err);
        setError("Unable to load projects. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    loadProjects();
  }, []);

  const categoryFilters = useMemo(() => {
    const categories = projects
      .map((project) => project.category)
      .filter(Boolean);

    return ["All", ...new Set(categories)];
  }, [projects]);

  const filtered = useMemo(() => {
    let list = projects;

    if (group !== "all") {
      list = list.filter((project) => project.groupKey === group);
    }

    if (category !== "All") {
      list = list.filter((project) => project.category === category);
    }

    return list;
  }, [projects, group, category]);

  const heading =
    group === "ongoing"
      ? "Ongoing Projects"
      : group === "completed"
        ? "Completed Projects"
        : "Our Projects";

  const subheading =
    group === "ongoing"
      ? "New addresses in the making."
      : group === "completed"
        ? "Landmarks we've delivered with pride."
        : "Every address we've shaped — past and present.";

  return (
    <div data-testid="projects-page" className="bg-[#FAFAFA]">
      {/* Hero */}
      <section className="pt-36 md:pt-44 pb-12 md:pb-16">
        <div className="container mx-auto px-6 md:px-12 max-w-6xl text-center">
          <FadeUp>
            <SectionLabel>
              {group === "all" ? "Projects" : heading}
            </SectionLabel>

            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight text-[#1A1A1A]">
              {subheading.split(".")[0]}
              <span className="italic brand-gradient-text">.</span>
            </h1>
          </FadeUp>
        </div>
      </section>

      {/* Group tabs */}
      <section className="pb-8">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <FadeUp className="flex justify-center gap-3 flex-wrap">
            {[
              { key: "all", label: "All", to: "/projects" },
              {
                key: "ongoing",
                label: "Ongoing",
                to: "/projects/ongoing",
              },
              {
                key: "completed",
                label: "Completed",
                to: "/projects/completed",
              },
            ].map((tab) => (
              <Link
                key={tab.key}
                to={tab.to}
                data-testid={`group-tab-${tab.key}`}
                className={`rounded-full px-6 py-2.5 font-medium text-sm md:text-base transition-all duration-300 ${
                  group === tab.key
                    ? "brand-gradient text-white shadow-md"
                    : "bg-white text-[#1A1A1A] border border-black/10 hover:border-[#7A2E5D]"
                }`}
              >
                {tab.label}
              </Link>
            ))}
          </FadeUp>
        </div>
      </section>

      {/* Category filter */}
      <section className="pb-14">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <FadeUp className="flex justify-center gap-2 md:gap-3 flex-wrap">
            {categoryFilters.map((filterCategory) => (
              <button
                key={filterCategory}
                type="button"
                onClick={() => setCategory(filterCategory)}
                data-testid={`category-${filterCategory.toLowerCase()}`}
                className={`rounded-full px-5 py-2 text-sm transition-all duration-300 ${
                  category === filterCategory
                    ? "bg-[#1A1A1A] text-white"
                    : "text-[#555] hover:text-[#7A2E5D]"
                }`}
              >
                {filterCategory}
              </button>
            ))}
          </FadeUp>
        </div>
      </section>

      {/* Project grid */}
      <section className="pb-24 md:pb-32">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          {loading && (
            <p className="text-center text-[#555] py-20">
              Loading projects...
            </p>
          )}

          {error && !loading && (
            <p className="text-center text-red-600 py-20">
              {error}
            </p>
          )}

          {!loading && !error && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                {filtered.map((project, index) => (
                  <FadeUp
                    key={project.id}
                    delay={(index % 3) * 0.08}
                  >
                    <Link
                      to={`/projects/${project.slug}`}
                      data-testid={`project-card-${project.slug}`}
                      className="group block bg-white overflow-hidden border border-black/5 shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all duration-300"
                      style={{ borderRadius: "20px" }}
                    >
                      <div
                        className="relative aspect-[4/5] overflow-hidden"
                        style={{
                          borderRadius: "20px 20px 0 0",
                        }}
                      >
                        {project.cover ? (
                          <img
                            src={project.cover}
                            alt={project.name}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.08]"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center text-[#555]">
                            No project image
                          </div>
                        )}

                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                        <span
                          data-testid={`category-badge-${project.slug}`}
                          className="absolute top-4 left-4 bg-white/95 backdrop-blur text-[#7A2E5D] text-[10px] font-semibold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full"
                        >
                          {project.category}
                        </span>

                        {project.statusKey === "completed" ? (
                          <span
                            data-testid={`completed-badge-${project.slug}`}
                            className="absolute top-4 right-4 bg-[#1A1A1A] text-white text-[10px] font-semibold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full"
                          >
                            {project.status || "Completed"}
                          </span>
                        ) : (
                          <span className="absolute top-4 right-4 brand-gradient text-white text-[10px] font-semibold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full">
                            {project.status || "In Progress"}
                          </span>
                        )}

                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                          <h3 className="font-serif text-2xl md:text-3xl leading-tight">
                            {project.name}
                          </h3>

                          <p className="text-sm mt-1 text-white/90 flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5" />
                            {project.locationShort}
                          </p>
                        </div>
                      </div>

                      <div className="px-6 py-5">
                        <p className="text-[#555] text-base md:text-lg">
                          {project.details}
                        </p>

                        <span className="mt-4 inline-flex items-center gap-1.5 text-[#7A2E5D] font-medium text-sm group-hover:gap-2.5 transition-all">
                          View More
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </Link>
                  </FadeUp>
                ))}
              </div>

              {filtered.length === 0 && (
                <p
                  className="text-center text-[#555] py-20"
                  data-testid="no-projects-empty"
                >
                  No projects yet in this category.
                </p>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}